import {memo, useMemo, useState} from 'react'
import PickupTime from '@/customComponents/sharedComponents/PickupTime.jsx'
import {Link, useNavigate} from 'react-router'
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/react'
import {mapImage, carIcon, greenTick, plusIconLight, rightArrow} from '@img'
import {useSidebar} from '@/context/SidebarContext.jsx'
import {locationLabel,
    locationCityShort,
    formatTime,
    formatDateTime,
    formatShortDate,
    formatTripPrice,
    formatPrice,
    getPageNumbers,
    getStatusStyle,
    getStatusLabel,
    parseCard} from '@/utils/functions'
import {addMinutesToAppZone} from '@/utils/dateUtils'
import CardBrand from '@/customComponents/uiComponents/global/CardBrand.jsx'
import CancelRide from '@/customComponents/uiComponents/global/CancelRide.jsx'
import {useMapConfig} from '@/hooks/useAppConfig'
import {staticRouteMapUrl} from '@/utils/staticMap'

const formatCents = (cents) => formatPrice(cents, {decimals: 2})
const isCancelledStatus = (status) => ['Cancelled', 'NoShow'].includes(status)


function tripHref(trip) {
    return `/rides/${trip.id}`
}

function PaymentHoldPill({trip, base}) {
    const ph = trip.payment_hold
    if (!ph) return null
    if (ph.is_captured === true) return (
        <span className={`${base} bg-(--bg-grey) inline-flex items-center justify-center gap-1.5`}>
            <img src={greenTick} alt="" className="h-3.5"/>
            Captured successfully
        </span>
    )
    if (ph.is_captured === false) return <span className={`${base} bg-[#fa8888]`}>Captured failed</span>
    if (ph.is_authorized === true) return <span className={`${base} bg-(--accent-green-light)`}>Pre-authorized hold placed</span>
    if (ph.is_authorized === false) return <span className={`${base} bg-[#fa8888]`}>Fail to hold charge</span>
    return null
}

// Status-driven label shown next to the trip row.
// - Cancelled → contextual label (who cancelled, or greenTick + "Refunded" on grey)
// - Completed → card pill (brand + last4) plus optional "Payment captured" pill
// - Other     → status label / status plus optional payment_hold pill (no icon)
export function StatusLabel({trip, className = ''}) {
    const base = 'font-medium px-4 py-1.5 rounded-full min-w-32 text-center text-(--text-primary) max-md:min-w-0 max-md:px-3 max-md:py-1 max-md:text-xs'

    if (isCancelledStatus(trip.status)) {
        // The status pill (who cancelled / refunded / no-show), plus — when the trip
        // response carries one — the passenger-facing cancellation reason next to it.
        // Truncated with the full text on hover; wraps to its own line on mobile.
        const reason = trip.cancellation_reason
        const reasonEl = reason ? (
            <span title={reason} className={`font-medium px-4 py-1.5 rounded-full text-sm max-md:px-3 max-md:py-1 max-md:text-xs bg-(--bg-grey) text-(--text-secondary) min-w-0 max-w-full md:max-w-[380px] truncate ${className}`}>
                {reason}
            </span>
        ) : null

        let mainEl
        if (trip.refund_status === 'refunded') {
            mainEl = (
                <span className={`${base} bg-(--bg-dark-grey) inline-flex items-center justify-center gap-1.5 ${className}`}>
                    <img src={greenTick} alt="" className="h-3.5"/>
                    Refunded
                </span>
            )
        } else if (trip.status === 'NoShow') {
            mainEl = <span className={`${base} bg-[#fa8888] ${className}`}>Passenger no show</span>
        } else {
            const by = (trip.cancelled_by || '').toLowerCase()
            let text
            if (by === 'driver') text = trip.cancellation_category_label || 'Cancelled by driver'
            else if (by === 'admin') text = 'Cancelled by admin'
            else if (by === 'passenger' || by === 'customer' || by === 'you') text = 'Cancelled by you'
            else text = trip.status_label || 'Cancelled'
            mainEl = <span className={`${base} bg-[#fa8888] ${className}`}>{text}</span>
        }

        return <>{mainEl}{reasonEl}</>
    }

    if (trip.status === 'Completed') {
        const {brand, last4} = parseCard(trip.selected_payment_method)
        return (
            <>
                <span className={`${base} bg-(--accent-green-light) inline-flex items-center justify-center gap-2 ${className}`}>
                    <CardBrand brand={brand} className="h-3.5"/>
                    **** {last4 || '----'}
                </span>
                <PaymentHoldPill trip={trip} base={base}/>
            </>
        )
    }

    return (
        <>
            <span className={`font-medium px-4 py-1.5 rounded-full min-w-32 text-center max-md:min-w-0 max-md:px-3 max-md:py-1 max-md:text-xs ${getStatusStyle(trip.status)} ${className}`}>{getStatusLabel(trip.status_label, trip.status)}</span>
            <PaymentHoldPill trip={trip} base={base}/>
        </>
    )
}

// Status-driven action button.
// - Cancelled → "Rebook", plus a "Credit issued" popover with the refund amount when one was issued
// - Completed → "Rebook" goes to /rebook/:id, which calls the endpoint and redirects
// - Draft     → no button
// - Other     → "Cancel" link
export function StatusButton({trip, rideType, className = '', tooltipAlign}) {
    const base = 'cursor-pointer font-medium px-5 py-1.5 rounded-full min-w-32 text-sm text-center max-md:min-w-0 max-md:px-4 max-md:py-1 max-md:text-xs'
    const navigate = useNavigate()

    // Same button on Completed and Cancelled rows: a cancelled/no-show trip can't be
    // returned (the backend refuses a return leg for one) but it CAN be rebooked.
    const rebookButton = (
        <button type="button" onClick={() => navigate(`/rebook/${trip.id}`)}
                className={`${base} bg-(--bg-grey) text-(--text-primary) border-0 ${className}`}>
            Rebook
        </button>
    )

    if (isCancelledStatus(trip.status)) {
        if (trip.refund_status !== 'refunded') return rebookButton
        const refundAmount = trip.refund_amount
        return (
            <>
                {rebookButton}
                <Popover className={`relative ${className}`}>
                    <PopoverButton className={`${base} bg-(--bg-grey) text-(--text-primary) border-0 inline-flex items-center justify-center gap-1.5`}>
                        Credit issued
                        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                        </svg>
                    </PopoverButton>
                    <PopoverPanel anchor="bottom end" transition className="mt-2 rounded-xl bg-white border border-(--border-color) shadow-lg px-4 py-3 z-[999] transition duration-200 ease-out data-[closed]:opacity-0 data-[closed]:scale-95">
                        <div className="flex items-center justify-between gap-6 min-w-[160px]">
                            <span className="text-sm text-(--text-secondary)">Refunded</span>
                            <span className="font-bold">{refundAmount != null ? formatCents(refundAmount) : '—'}</span>
                        </div>
                    </PopoverPanel>
                </Popover>
            </>
        )
    }
    if (trip.status === 'Completed') {
        return (
            <>
                {rebookButton}
                {!trip.parent_trip && (() => {
                    // An initial trip that already spawned a real (non-Draft) return booking can't get another.
                    const hasReturnBooking = !!trip.return_booking && trip.return_booking.status !== 'Draft'
                    return (
                        <span className="relative group inline-flex">
                            <button type="button" onClick={() => navigate(`/return-booking/${trip.id}`)}
                                    disabled={hasReturnBooking}
                                    className={`${base} flex items-center justify-center gap-2 bg-(--text-primary) text-white border-0 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
                                <img alt="" src={plusIconLight} className="w-4 h-4"/>
                                Return Booking
                            </button>
                            {hasReturnBooking && (
                                <span className="hidden group-hover:block absolute top-full right-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:right-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">This trip already has a return booking: {trip.return_booking.id}</span>
                            )}
                        </span>
                    )
                })()}
            </>
        )
    }
    if (trip.status === 'Draft') return null
    // No `disabled` prop by design — CancelRide applies the shared gate itself, so a
    // started ride disables here too (with the hover tooltip explaining why).
    return (
        <CancelRide trip={trip} tooltipAlign={tooltipAlign} className={`${base} bg-(--bg-grey) text-(--text-red) border-0 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}>
            Cancel
        </CancelRide>
    )
}

// Compact card for the home mobile slider
function HomeSliderCard({trip, rideType}) {
    const durationMin = trip.route?.duration_minutes
    const endTime = trip.scheduled_at && durationMin
        ? addMinutesToAppZone(trip.scheduled_at, durationMin)
        : null
    return (
        <Link to={tripHref(trip)} className="bg-white rounded-2xl p-3 block border-8 border-(--bg-grey)">
            <div className="flex items-center gap-2 max-md:flex-wrap">
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-(--text-secondary) mb-1! truncate max-md:text-xs">{locationCityShort(trip.origin)}</p>
                    <p className="font-bold text-xl mb-0! max-md:text-base"><PickupTime trip={trip} format={formatTime}/></p>
                    <p className="text-sm bold mb-0! max-md:text-xs">{formatShortDate(trip.scheduled_at)}</p>
                </div>
                <span className="w-9 h-9 bg-(--accent-green-light) rounded-full flex items-center justify-center shrink-0">
                    <img src={carIcon} alt="" className="w-4 h-4"/>
                </span>
                <div className="flex-1 min-w-0 text-right">
                    <p className="text-sm text-(--text-secondary) mb-1! truncate max-md:text-xs">{locationCityShort(trip.destination)}</p>
                    <p className="font-bold text-xl mb-0! max-md:text-base">{endTime ? formatTime(endTime) : '--'}</p>
                    <p className="text-sm bold mb-0! max-md:text-xs">{endTime ? formatShortDate(endTime) : ''}</p>
                </div>
            </div>
        </Link>
    )
}

// Whole-row navigation, mirroring the notifications list: the entire card is the link
// target instead of just the map thumb + address.
//
// A plain <Link> wrapper is NOT usable here — the action cluster holds <button>s (Cancel /
// Rebook / Return Booking) and a Popover, and interactive content may not be nested inside
// an anchor. So the row carries role="link" + Enter/Space, exactly like the notification rows.
function useRowLink(trip) {
    const navigate = useNavigate()
    const href = tripHref(trip)
    return {
        role: 'link',
        tabIndex: 0,
        onClick: () => navigate(href),
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(href) }
        },
    }
}

// Put on the action cluster so its buttons don't also trigger the row's navigation.
const stopRowNav = (e) => e.stopPropagation()

// …and this kills the row's `cursor-pointer` over that cluster. The pills in it (status,
// cancellation reason, payment hold, card) are labels, not controls, so a hand cursor over
// them promises a click that does nothing — the cluster already stops the row navigation.
// Every real button inside carries its own `cursor-pointer`, which wins over this.
const statusClusterCursor = 'cursor-context-menu'

// Mobile card for listing pages (vertical list) — the driver portal's card: the route
// on a static map with the time and fare floating on it, then the trip number and the
// two addresses on grey, then this ride's status pills and actions inside the same card.
function ListingMobileCard({trip, rideType}) {
    const rowLink = useRowLink(trip)
    const {token, styleUrl} = useMapConfig()
    const mapUrl = staticRouteMapUrl(trip, {token, styleUrl})

    return (
        // ⚠️ NO overflow-hidden on the card — it clipped the Cancel tooltip. The map
        // clips its own top corners instead.
        <div {...rowLink} className="cursor-pointer rounded-2xl bg-(--bg-grey)">
            <div className="relative h-40 overflow-hidden rounded-t-2xl bg-gray-200">
                <img src={mapUrl || mapImage} alt="" loading="lazy" className="h-full w-full object-cover"/>
                <span className="absolute top-2.5 left-2.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-(--text-primary)">
                    <PickupTime trip={trip} format={formatDateTime}/>
                </span>
                <span className="absolute top-2.5 right-2.5 rounded-full bg-white px-3 py-1 text-sm font-bold text-(--text-primary)">
                    {formatTripPrice(trip)}
                </span>
            </div>

            <div className="p-3">
                <p className="mb-1.5! text-xs font-medium text-(--text-secondary)">Trip #{trip.id}</p>
                <div className="flex flex-col gap-3">
                    <div className="flex items-stretch gap-2.5">
                        {/* Dot, then the rail filling the rest of this row AND the gap below
                            it (-mb-3), so it ends exactly at the drop-off pin. */}
                        <span className="flex shrink-0 flex-col items-center">
                            <span className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-(--text-primary)">
                                <span className="h-1.5 w-1.5 rounded-full bg-(--text-primary)"/>
                            </span>
                            <span className="mt-1 -mb-3 min-h-3 flex-1 border-l-2 border-dashed border-(--text-secondary)" aria-hidden="true"/>
                        </span>
                        <span className="min-w-0 text-sm text-(--text-primary)">{locationLabel(trip.origin)}</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0" aria-hidden="true">
                            <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="12" cy="9.5" r="2.5" fill="currentColor"/>
                        </svg>
                        <span className="min-w-0 text-sm text-(--text-primary)">{locationLabel(trip.destination)}</span>
                    </div>
                </div>

                {/* `relative` — the Cancel tooltip spans this row ('full'), so it can't
                    run off either screen edge whichever side the button wraps to. */}
                <div className={`relative mt-3 flex flex-wrap items-center gap-2 ${statusClusterCursor}`} onClick={stopRowNav}>
                    <StatusLabel trip={trip}/>
                    <StatusButton trip={trip} rideType={rideType} tooltipAlign="full"/>
                </div>
            </div>
        </div>
    )
}

// Desktop row (used inside listing / home right column)
function DesktopRow({trip, rideType}) {
    const rowLink = useRowLink(trip)
    return (
        <div {...rowLink} className="border-b-2 border-dashed border-(--text-secondary) pb-3 mb-3 cursor-pointer">
            <div className="flex gap-4">
                <div className="w-14 h-14 overflow-hidden shrink-0 bg-gray-100 rounded-xl">
                    <img src={mapImage} alt="map" className="w-full h-full object-cover"/>
                </div>
                <div className="flex-1">
                    <span className="block mb-1">{locationLabel(trip.origin)}</span>
                    <span className="text-(--text-secondary) block mb-2"><PickupTime trip={trip} format={formatDateTime} inline/>{trip.reference && <> &middot; {trip.reference}</>}</span>
                </div>
                <span className="font-bold text-2xl pt-2">{formatTripPrice(trip)}</span>
            </div>
            <div className={`flex justify-start xl:justify-end mt-0 gap-2 ${statusClusterCursor}`} onClick={stopRowNav}>
                <StatusLabel trip={trip}/>
                <StatusButton trip={trip} rideType={rideType}/>
            </div>
        </div>
    )
}

const Pagination = memo(function Pagination({page, totalPages, onPageChange, canPrev, canNext}) {
    if (totalPages <= 1 || !onPageChange) return null
    return (
        <div className="flex items-center justify-center gap-2 max-md:gap-1">
            <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={!canPrev}
                    className="cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm disabled:opacity-30 disabled:cursor-default">
                <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M6.5 1L1.5 6L6.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {getPageNumbers(page, totalPages).map((p, i) => (
                p === '...'
                    ? <span key={`dots-${i}`} className="w-9 h-9 flex items-center justify-center text-(--text-secondary) max-md:h-7 max-md:w-7 max-md:text-sm">...</span>
                    : <button key={p} onClick={() => onPageChange(p)}
                              className={`cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm font-medium ${p === page ? 'bg-(--bg-dark) text-white' : ''}`}>
                        {p}
                    </button>
            ))}
            <button onClick={() => onPageChange(page + 1)} disabled={!canNext}
                    className="cursor-pointer w-9 h-9 rounded-full flex items-center justify-center max-md:h-7 max-md:w-7 max-md:text-sm disabled:opacity-30 disabled:cursor-default">
                <svg width="8" height="12" viewBox="0 0 8 12" fill="none"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
        </div>
    )
})

export default function TripListing({
    trips,
    heading,
    showDark = true,
    rideType = 'upcoming',
    isLoading,
    isFetching,
    pagination,
    page,
    onPageChange,
    footer,
    headingArrow = false,
}) {
    const {isMobile} = useSidebar()
    // Optional right-arrow icon after the section heading (only the Completed page opts in).
    const headingNode = <>{heading}{headingArrow && <img src={rightArrow} alt="" className="w-4 h-auto"/>}</>

    const {totalPages, canPrev, canNext} = useMemo(() => {
        const tp = pagination?.last_page ?? 1
        return {
            totalPages: tp,
            canPrev: pagination ? page > 1 : false,
            canNext: pagination ? page < tp : false,
        }
    }, [pagination, page])

    if (isLoading) {
        if (isMobile && showDark) {
            return (
                <div className="flex gap-2.5 overflow-hidden animate-pulse w-full min-w-0">
                    {[1, 2].map(i => (
                        <div key={i} className="bg-white rounded-2xl p-3 shrink-0 w-[80%] shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
                            <div className="flex items-center gap-2 max-md:flex-wrap">
                                <div className="flex-1 space-y-2">
                                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                </div>
                                <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0"></div>
                                <div className="flex-1 space-y-2">
                                    <div className="h-3 bg-gray-200 rounded w-3/4 ml-auto"></div>
                                    <div className="h-4 bg-gray-200 rounded w-1/2 ml-auto"></div>
                                    <div className="h-3 bg-gray-200 rounded w-2/3 ml-auto"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )
        }
        if (isMobile && !showDark) {
            return (
                <div className="flex flex-col gap-3 animate-pulse">
                    {/* The mobile card's silhouette (ListingMobileCard): map with the time
                        and fare pills, trip number, two addresses, the status/action pills. */}
                    {[1, 2, 3].map(i => (
                        <div key={i} className="overflow-hidden rounded-2xl bg-(--bg-grey)">
                            <div className="relative h-40 bg-gray-200">
                                <span className="absolute top-2.5 left-2.5 h-6 w-36 rounded-full bg-white/80"></span>
                                <span className="absolute top-2.5 right-2.5 h-6 w-14 rounded-full bg-white/80"></span>
                            </div>
                            <div className="p-3">
                                <div className="h-2.5 w-16 bg-gray-300 rounded mb-2.5"></div>
                                <div className="flex gap-2.5">
                                    <div className="h-4 w-4 bg-gray-300 rounded-full shrink-0"></div>
                                    <div className="flex-1 space-y-1.5">
                                        <div className="h-3 bg-gray-300 rounded w-11/12"></div>
                                        <div className="h-3 bg-gray-300 rounded w-3/5"></div>
                                    </div>
                                </div>
                                <div className="flex gap-2.5 mt-3">
                                    <div className="h-4 w-4 bg-gray-300 rounded-full shrink-0"></div>
                                    <div className="h-3 bg-gray-300 rounded w-4/5"></div>
                                </div>
                                <div className="flex items-center gap-2 mt-3">
                                    <div className="h-6 w-24 bg-gray-300 rounded-full"></div>
                                    <div className="h-6 w-28 bg-gray-300 rounded-full"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )
        }
        return (
            <div className="white-global-section">
                <h5 className="mb-0! font-bold max-md:text-base!">{heading}</h5>
                <div className="animate-pulse space-y-4 mt-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex gap-4 border-b-2 border-dashed border-gray-200 py-4 pb-2">
                            <div className="w-14 h-14 rounded-sm bg-gray-200"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const fetchingClass = isFetching ? 'opacity-60 transition-opacity duration-200' : ''

    if (isMobile && showDark) {
        return (
            <div className={`flex flex-col gap-4 w-full min-w-0 ${fetchingClass}`}>
                {trips.length === 0 ? (
                    <p className="text-(--text-secondary) mb-0!">No rides found</p>
                ) : (
                    <div className="flex gap-2.5 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {trips.map((trip) => (
                            <div key={trip.id} className="shrink-0 w-[80%] snap-start">
                                <HomeSliderCard trip={trip} rideType={rideType}/>
                            </div>
                        ))}
                    </div>
                )}
                {footer}
            </div>
        )
    }

    if (isMobile && !showDark) {
        return (
            <div className="flex flex-col gap-5">
                <div className={`flex flex-col gap-3 ${fetchingClass}`}>
                    {/* No category title on the mobile list — the selected tab above already says it. */}
                    {trips.length === 0 ? (
                        <p className="text-(--text-secondary) mb-0!">No rides found</p>
                    ) : (
                        trips.map((trip) => (
                            <ListingMobileCard key={trip.id} trip={trip} rideType={rideType}/>
                        ))
                    )}
                    {footer}
                </div>
                <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} canPrev={canPrev} canNext={canNext}/>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <div className={`white-global-section ${fetchingClass}`}>
                <h5 className="font-bold mb-4 flex items-center gap-2 max-md:text-base!">{headingNode}</h5>
                {trips.length === 0 ? (
                    <p className="text-(--text-secondary) mt-4 mb-0!">No rides found</p>
                ) : (
                    <div className="flex flex-col">
                        {trips.map((trip) => (
                            <DesktopRow key={trip.id} trip={trip} rideType={rideType}/>
                        ))}
                    </div>
                )}
                {footer}
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} canPrev={canPrev} canNext={canNext}/>
        </div>
    )
}
